<main class="page" [attr.aria-labelledby]="titleId">
  <header class="page-header">
    <div class="title-block">
      @if (eyebrow) {
        <span class="eyebrow">
          @if (eyebrowIcon) { <mat-icon>{{ eyebrowIcon }}</mat-icon> }
          {{ eyebrow }}
        </span>
      }
      <h1 [id]="titleId">{{ title }}</h1>
      @if (description) { <p>{{ description }}</p> }
    </div>

    <div class="header-actions">
      <ng-content select="[table-page-header-extra]"></ng-content>
      @for (action of actions; track action.id) {
        @switch (action.appearance) {
          @case ('stroked') {
            <button mat-stroked-button [color]="action.tone ?? 'primary'" (click)="triggerPageAction(action)"
              [disabled]="action.disabled || action.loading" [matTooltip]="action.tooltip ?? ''">
              @if (action.loading) { <mat-spinner diameter="18"></mat-spinner> }
              @else if (action.icon) { <mat-icon>{{ action.icon }}</mat-icon> }
              {{ action.label }}
            </button>
          }
          @case ('basic') {
            <button mat-button [color]="action.tone ?? 'primary'" (click)="triggerPageAction(action)"
              [disabled]="action.disabled || action.loading" [matTooltip]="action.tooltip ?? ''">
              @if (action.loading) { <mat-spinner diameter="18"></mat-spinner> }
              @else if (action.icon) { <mat-icon>{{ action.icon }}</mat-icon> }
              {{ action.label }}
            </button>
          }
          @case ('icon') {
            <button mat-icon-button [color]="action.tone ?? 'primary'" (click)="triggerPageAction(action)"
              [disabled]="action.disabled || action.loading" [matTooltip]="action.tooltip ?? action.label"
              [attr.aria-label]="action.label">
              @if (action.loading) { <mat-spinner diameter="20"></mat-spinner> }
              @else { <mat-icon>{{ action.icon ?? 'more_horiz' }}</mat-icon> }
            </button>
          }
          @default {
            <button mat-flat-button [color]="action.tone ?? 'primary'" (click)="triggerPageAction(action)"
              [disabled]="action.disabled || action.loading" [matTooltip]="action.tooltip ?? ''">
              @if (action.loading) { <mat-spinner diameter="18"></mat-spinner> }
              @else if (action.icon) { <mat-icon>{{ action.icon }}</mat-icon> }
              {{ action.label }}
            </button>
          }
        }
      }
    </div>
  </header>

  @if (showSummary) {
    <section class="overview-strip" aria-label="Summary">
      @for (item of effectiveSummaryItems; track item.label) {
        <div class="overview-item">
          @if (item.icon) { <mat-icon>{{ item.icon }}</mat-icon> }
          <span>
            <strong class="overview-value">{{ summaryValue(item) }}</strong>
            <span class="overview-label">{{ item.label }}</span>
          </span>
        </div>
      }
    </section>
  }

  <section class="workspace" [class.without-summary]="!showSummary"
    [class.inspector-open]="selectedRow && inspector">
    <div class="list-pane">
      @if (searchable || filters.length || refreshable) {
        <div class="toolbar">
          @if (searchable) {
            <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
              <mat-icon matPrefix>search</mat-icon>
              <mat-label>{{ searchPlaceholder }}</mat-label>
              <input matInput [ngModel]="searchTerm" (ngModelChange)="setSearchTerm($event)">
              @if (searchTerm) {
                <button matSuffix mat-icon-button aria-label="Clear search" (click)="clearSearch()">
                  <mat-icon>close</mat-icon>
                </button>
              }
            </mat-form-field>
          }

          @for (filter of filters; track filter.id) {
            <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
              <mat-label>{{ filter.label }}</mat-label>
              <mat-select [ngModel]="filterValues[filter.id]"
                (selectionChange)="setFilter(filter.id, $event.value)">
                <mat-option [value]="null">{{ filter.allLabel ?? 'All' }}</mat-option>
                @for (option of filter.options; track option.label) {
                  <mat-option [value]="option.value">{{ option.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }

          <ng-content select="[table-page-toolbar-extra]"></ng-content>

          @if (refreshable) {
            <button mat-icon-button [matTooltip]="refreshTooltip" [attr.aria-label]="refreshTooltip"
              (click)="refreshRequested.emit()" [disabled]="loading || refreshing">
              @if (refreshing) { <mat-spinner diameter="20"></mat-spinner> }
              @else { <mat-icon>refresh</mat-icon> }
            </button>
          }
        </div>
      }

      @if (loading) {
        <div class="state-view" aria-live="polite">
          <mat-spinner diameter="36"></mat-spinner>
          <p>{{ loadingMessage }}</p>
        </div>
      } @else if (errorMessage) {
        <div class="state-view error-state" role="alert">
          <mat-icon>{{ errorIcon }}</mat-icon>
          <h2>{{ errorTitle }}</h2>
          <p>{{ errorMessage }}</p>
          <button mat-stroked-button color="primary" (click)="retryRequested.emit()">{{ retryLabel }}</button>
        </div>
      } @else if (data.length === 0) {
        <div class="state-view empty-state">
          <span class="empty-icon"><mat-icon>{{ emptyState.icon ?? 'inbox' }}</mat-icon></span>
          <h2>{{ emptyState.title ?? 'No items yet' }}</h2>
          @if (emptyState.description) { <p>{{ emptyState.description }}</p> }
          @if (emptyAction; as action) {
            <button mat-flat-button [color]="action.tone ?? 'primary'" (click)="triggerPageAction(action)"
              [disabled]="action.disabled || action.loading">
              @if (action.icon) { <mat-icon>{{ action.icon }}</mat-icon> }
              {{ action.label }}
            </button>
          }
        </div>
      } @else if (filteredRows.length === 0) {
        <div class="state-view">
          <mat-icon>search_off</mat-icon>
          <h2>{{ noResultsTitle }}</h2>
          <p>{{ noResultsDescription }}</p>
        </div>
      } @else {
        <div class="table-wrap">
          <table mat-table matSort [dataSource]="visibleRows" [trackBy]="trackRow" [attr.aria-label]="ariaLabel"
            [matSortActive]="sortActive" [matSortDirection]="sortDirection" (matSortChange)="handleSort($event)">
            @for (column of columns; track column.key) {
              <ng-container [matColumnDef]="column.key">
                <th mat-header-cell *matHeaderCellDef [mat-sort-header]="column.key"
                  [disabled]="!isColumnSortable(column)" [style.width]="column.width"
                  [class.align-center]="column.align === 'center'" [class.align-end]="column.align === 'end'">
                  {{ column.label }}
                </th>
                <td mat-cell *matCellDef="let row" [ngClass]="cellClass(row, column)"
                  [class.align-center]="column.align === 'center'" [class.align-end]="column.align === 'end'">
                  @if (cellTemplate(column.key); as template) {
                    <ng-container [ngTemplateOutlet]="template"
                      [ngTemplateOutletContext]="{ $implicit: row, row: row, value: cellValue(row, column), column: column }">
                    </ng-container>
                  } @else {
                    @switch (column.type) {
                      @case ('icon') {
                        <span class="type-mark" [attr.data-tone]="toneFor(row, column.tone)">
                          <mat-icon>{{ iconFor(row, column.icon) }}</mat-icon>
                        </span>
                      }
                      @case ('badge') {
                        <span class="status-badge" [attr.data-tone]="toneFor(row, column.tone)">
                          {{ displayCellValue(row, column) }}
                        </span>
                      }
                      @default {
                        <span class="cell-primary">{{ displayCellValue(row, column) }}</span>
                        @if (column.secondaryValue) {
                          <span class="cell-secondary">{{ secondaryCellValue(row, column) }}</span>
                        }
                      }
                    }
                  }
                </td>
              </ng-container>
            }

            @if (rowActions.length) {
              <ng-container matColumnDef="__actions">
                <th mat-header-cell *matHeaderCellDef><span class="visually-hidden">Actions</span></th>
                <td mat-cell *matCellDef="let row" class="actions-cell">
                  <button mat-icon-button [matMenuTriggerFor]="rowMenu" aria-label="Row actions"
                    (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()" (keydown.space)="$event.stopPropagation()">
                    <mat-icon>more_horiz</mat-icon>
                  </button>
                  <mat-menu #rowMenu="matMenu">
                    @for (action of rowActions; track action.id) {
                      @if (isRowActionVisible(action, row)) {
                        <button mat-menu-item [class.danger-action]="action.tone === 'warn'"
                          [disabled]="isRowActionDisabled(action, row) || isRowActionLoading(action, row)"
                          (click)="triggerRowAction(action, row)">
                          @if (isRowActionLoading(action, row)) { <mat-spinner diameter="18"></mat-spinner> }
                          @else if (action.icon) { <mat-icon>{{ action.icon }}</mat-icon> }
                          <span>{{ action.label }}</span>
                        </button>
                      }
                    }
                  </mat-menu>
                </td>
              </ng-container>
            }

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns"
              [class.selectable]="canSelectRows()" [class.selected]="isSelected(row)"
              [attr.tabindex]="canSelectRows() ? 0 : null" (click)="selectRow(row)"
              (keydown.enter)="selectRow(row)" (keydown.space)="selectRow(row); $event.preventDefault()">
            </tr>
          </table>
        </div>

        @if (pagination) {
          <mat-paginator [length]="serverPagination ? totalItems : filteredRows.length"
            [pageIndex]="pageIndex" [pageSize]="pageSize"
            [pageSizeOptions]="pageSizeOptions" (page)="handlePage($event)" showFirstLastButtons>
          </mat-paginator>
        }
      }
    </div>

    @if (selectedRow && inspector; as itemInspector) {
      <aside class="inspector" [attr.aria-label]="itemInspector.ariaLabel ?? 'Selected row details'">
        <div class="inspector-heading">
          @if (itemInspector.icon) {
            <span class="type-mark large" [attr.data-tone]="toneFor(selectedRow, itemInspector.tone)">
              <mat-icon>{{ iconFor(selectedRow, itemInspector.icon) }}</mat-icon>
            </span>
          }
          <div>
            @if (itemInspector.subtitle) { <span>{{ inspectorValue(selectedRow, itemInspector.subtitle) }}</span> }
            <h2>{{ inspectorValue(selectedRow, itemInspector.title) }}</h2>
          </div>
          <button mat-icon-button aria-label="Close details" (click)="closeInspector()"><mat-icon>close</mat-icon></button>
        </div>

        @if (itemInspector.description) {
          <p class="inspector-description">{{ inspectorValue(selectedRow, itemInspector.description) }}</p>
        }

        @if (itemInspector.fields?.length) {
          <dl>
            @for (field of itemInspector.fields; track field.label) {
              <div>
                <dt>{{ field.label }}</dt>
                <dd [class.monospace]="field.monospace">{{ displayInspectorValue(selectedRow, field) }}</dd>
              </div>
            }
          </dl>
        }

        @if (itemInspector.actions?.length) {
          <div class="inspector-actions">
            @for (action of itemInspector.actions; track action.id) {
              @if (isRowActionVisible(action, selectedRow)) {
                <button mat-stroked-button [color]="action.tone ?? 'primary'"
                  [disabled]="isRowActionDisabled(action, selectedRow) || isRowActionLoading(action, selectedRow)"
                  (click)="triggerRowAction(action, selectedRow)">
                  @if (isRowActionLoading(action, selectedRow)) { <mat-spinner diameter="18"></mat-spinner> }
                  @else if (action.icon) { <mat-icon>{{ action.icon }}</mat-icon> }
                  {{ action.label }}
                </button>
              }
            }
          </div>
        }

        @if (itemInspector.note) {
          <div class="inspector-note">
            <mat-icon>{{ itemInspector.noteIcon ?? 'info' }}</mat-icon>
            <span>{{ itemInspector.note }}</span>
          </div>
        }
      </aside>
    }
  </section>

  <ng-content select="[appTableCell]"></ng-content>
</main>
